/**
 * A camada de voz atrás de uma interface só.
 *
 * O ADR-007 decidiu que escuta e fala são peças separadas e recombináveis:
 * `hybrid` escuta com Realtime e fala com OmniVoice, `realtime` faz as duas
 * coisas pelo mesmo canal, `text` escuta e não fala. Se o resto da aplicação
 * conhecesse essas combinações, cada tela precisaria saber de qual modo veio o
 * áudio. Conhece só `VoiceEngine`; quem compõe é o `engine.ts`.
 *
 * Por isso `Listener` e `Speaker` existem: são os dois lados que o engine
 * encaixa. Não são para consumo fora desta pasta.
 */

import type { VoiceMode } from '@will/contracts';

/** `final` distingue transcrição parcial (rascunho na tela) de turno fechado. */
export type HeardHandler = (text: string, final: boolean) => void;

/** Nível de entrada do microfone, 0..1 — alimenta a animação do domo. */
export type LevelHandler = (level: number) => void;

export interface VoiceEngine {
  start(): Promise<void>;
  stop(): void;
  /**
   * Fala o texto. `estado` é a expressão do domo no momento da fala e escolhe o
   * perfil de prosódia do lado do servidor — opcional de propósito: sem ele o
   * perfil é neutro, que é o comportamento anterior a esta camada.
   */
  speak(text: string, estado?: string): Promise<void>;
  hush(): void;
  onHeard(cb: HeardHandler): void;
  onLevel(cb: LevelHandler): void;
  readonly mode: VoiceMode;
}

// ---------------------------------------------------------------------------
// Peças internas
// ---------------------------------------------------------------------------

export interface Listener {
  start(): Promise<void>;
  stop(): void;
  onHeard(cb: HeardHandler): void;
  onLevel(cb: LevelHandler): void;
  /**
   * O engine avisa quando o Will começa e termina de falar, e com que texto.
   *
   * O ouvinte precisa das duas informações por motivos diferentes: `talking`
   * habilita o barge-in, e `text` habilita a guarda de eco — sem saber o que
   * está sendo dito não há como distinguir a voz do Will voltando pelo
   * microfone de uma frase real do usuário.
   */
  setTalking(talking: boolean, text?: string): void;
  /** Disparado quando o usuário fala por cima. O engine responde com hush(). */
  onBargeIn(cb: () => void): void;
}

export interface Speaker {
  /** Resolve quando o áudio termina — ou quando hush() o interrompe. */
  /**
   * Fala o texto. `estado` é a expressão do domo no momento da fala e escolhe o
   * perfil de prosódia do lado do servidor — opcional de propósito: sem ele o
   * perfil é neutro, que é o comportamento anterior a esta camada.
   */
  speak(text: string, estado?: string): Promise<void>;
  hush(): void;
}

// ---------------------------------------------------------------------------
// Erros
// ---------------------------------------------------------------------------

/**
 * O Realtime não subiu e a queda para o `listenBrowser` é legítima.
 *
 * Tipo próprio em vez de checar `status === 503` no chamador: o engine decide
 * a queda pelo tipo do erro, e o motivo (sem chave da OpenAI, cota, serviço
 * fora) fica no texto sem virar contrato.
 */
export class RealtimeUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RealtimeUnavailableError';
  }
}

// ---------------------------------------------------------------------------
// Web Speech API — tipagem mínima
// ---------------------------------------------------------------------------

/**
 * O lib.dom do TypeScript 5.9 declara `SpeechRecognitionResult` e companhia,
 * mas não declara `SpeechRecognition` nem o prefixo `webkit`. Declaramos aqui
 * só o que usamos, como tipo exportado e não como `declare global`: augmentar
 * o escopo global desta pasta afetaria os outros módulos do app.
 */
export interface SpeechRecognitionResultEventLike extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

export interface SpeechRecognitionErrorEventLike extends Event {
  readonly error: string;
  readonly message?: string;
}

export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((ev: SpeechRecognitionResultEventLike) => void) | null;
  onerror: ((ev: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

export interface SpeechRecognitionCtor {
  new (): SpeechRecognitionLike;
}

export interface WindowComVoz {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

/** Normaliza o que veio de um `catch`, que em TS é `unknown`. */
export function comoErro(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e));
}
